// ETF Wealth Builder Calculator -- the feature orchestrator.
//
// Composes the whole experience: the input form (left), and on the right either
// an empty prompt or the KPI strip + probability Fan Chart + Conversion CTA.
// All network state (isPending / data / error) comes from the single TanStack
// mutation; the child components stay pure and stateless.

import React from 'react';

import { useSimulateSavings } from './api/queries';
import { CalculatorForm } from './components/CalculatorForm';
import { ConversionCTA } from './components/ConversionCTA';
import { FanChart } from './components/FanChart';

export const EtfWealthBuilderCalculator: React.FC = () => {
    const { mutate, data: result, isPending, error } = useSimulateSavings();

    const formatCurrency = (val: number, isUcits: boolean) =>
        new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: isUcits ? 'EUR' : 'USD',
            maximumFractionDigits: 0,
        }).format(val);

    return (
        <div className="max-w-7xl mx-auto p-4 lg:p-8 font-sans">
            <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white mb-4 tracking-tight">
                    ETF Wealth Builder Simulator
                </h2>
                <p className="text-slate-600 dark:text-slate-300 max-w-2xl mx-auto text-lg">
                    Please, do not rely on "average returns." Markets never move in straight lines. Our advanced
                    simulation engine analyzes 10,000 possible market scenarios to show the probability of
                    achieving your retirement and wealth-building goals, helping you make smarter investment
                    decisions with confidence. On our Forge Ultra wealth-building platform you can use multiple
                    scenarios to analyze performance of your investments.
                    Create Premium or Professional Account to benefit the most.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4 h-fit">
                    <CalculatorForm
                        onSubmit={mutate}
                        isPending={isPending}
                        error={error}
                    />
                </div>

                <div className="lg:col-span-8 flex flex-col">
                    {!result ? (
                        <div className="h-full min-h-[500px] flex items-center justify-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                            <p className="text-slate-400 font-medium px-4 text-center text-lg">
                                Configure plan parameters and run the simulation.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-6 transition-opacity duration-500 opacity-100">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 border-l-4 border-l-slate-400">
                                    <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">
                                        Total Invested Amount
                                    </p>
                                    <p className="text-2xl font-bold text-slate-800">
                                        {formatCurrency(result.final_contributed, result.is_ucits)}
                                    </p>
                                </div>
                                <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 border-l-4 border-l-sky-500">
                                    <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">
                                        Expected Amount(Median)
                                    </p>
                                    <p className="text-2xl font-bold text-sky-600">
                                        {formatCurrency(result.final_median_value, result.is_ucits)}
                                    </p>
                                </div>
                                <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 border-l-4 border-l-amber-400">
                                    <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">
                                        Your Purchasing Power
                                    </p>
                                    <p className="text-2xl font-bold text-amber-500">
                                        {formatCurrency(result.final_real_value, result.is_ucits)}
                                    </p>
                                </div>
                            </div>

                            <FanChart data={result.yearly_data} isUcits={result.is_ucits} />
                            <ConversionCTA result={result} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
